import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import {
    ListColgroup,
    listMinWidth,
    PatientListHeadCell,
    type ListTableColumn,
} from '../PatientList/PatientListTable';
import CourseStageHeader from './CourseStageHeader';
import { ListPane, ListSurface } from './ListSurface';

type PatientTablePageProps = {
    /** The block above the list. */
    toolbar: ReactNode;
    columns: readonly ListTableColumn[];
    /** The narrowest the flexible column may get, in px; below the table width this gives, the card scrolls. */
    flexMinTrack: number;
    /**
     * The table's rows, each keyed by its patient's id and version, so a row
     * rebuilds from what the connector stored after each write.
     */
    rows: ReactNode;
    /** What follows the rows inside the card, such as a load-more footer. */
    children?: ReactNode;
    /** A note under the rows, such as why there are none. */
    message?: string;
    /** Whether the list is not known yet; a spinner then stands in for the rows. */
    isLoading: boolean;
    /** Whether the list failed to load; it is said in place of the rows, so no partial list is shown. */
    isError: boolean;
};

/**
 * A patient list page: its toolbar above a card holding a table of the given
 * columns, the table's rows, and what is said while it loads, when it holds
 * nobody or when it failed to load.
 */
export default function PatientTablePage({
    toolbar,
    columns,
    flexMinTrack,
    rows,
    children,
    message,
    isLoading,
    isError,
}: PatientTablePageProps) {
    const intl = useIntl();
    const tracks = columns.map((column) => column.track);

    return (
        <>
            {toolbar}
            <ListPane>
                <ListSurface>
                    <Box sx={{ overflow: 'auto', flex: 1 }}>
                        <Table
                            stickyHeader
                            sx={{
                                tableLayout: 'fixed',
                                minWidth: listMinWidth(tracks, flexMinTrack),
                            }}
                        >
                            <ListColgroup tracks={tracks} />
                            <TableHead>
                                <TableRow>
                                    {columns.map((column, index) => (
                                        <PatientListHeadCell
                                            key={index}
                                            alignEnd={column.alignEnd}
                                        >
                                            {column.labelId &&
                                                (column.stageNames ? (
                                                    <>
                                                        <Box
                                                            component="span"
                                                            sx={visuallyHidden}
                                                        >
                                                            {intl.formatMessage(
                                                                {
                                                                    id: column.labelId,
                                                                }
                                                            )}
                                                        </Box>
                                                        <CourseStageHeader />
                                                    </>
                                                ) : (
                                                    intl.formatMessage({
                                                        id: column.labelId,
                                                    })
                                                ))}
                                        </PatientListHeadCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!isLoading && !isError && rows}
                            </TableBody>
                        </Table>
                        {isLoading && (
                            <Box display="flex" justifyContent="center" py={6}>
                                <CircularProgress />
                            </Box>
                        )}
                        {children}
                        {message !== undefined && (
                            <Typography variant="body2" sx={{ px: 3, py: 2 }}>
                                {message}
                            </Typography>
                        )}
                        {isError && (
                            <Typography
                                variant="body2"
                                color="error"
                                role="alert"
                                sx={{ px: 3, py: 2 }}
                            >
                                {intl.formatMessage({
                                    id: 'patientList.error.load',
                                })}
                            </Typography>
                        )}
                    </Box>
                </ListSurface>
            </ListPane>
        </>
    );
}
